/**
 * Browser stream + tabs handlers — all browser:* socket events.
 *
 *   BROWSER_VIEW_STATE               → the server-owned view-state snapshot
 *                                      (the remote neko pane's sole truth).
 *   BROWSER_LOCAL_READY/STOPPED      → local in-app browser loopback signal
 *                                      (LOCAL_BROWSER_V1) — mirrors STREAM
 *                                      READY/STOPPED for the 'local' source.
 *   BROWSER_TABS_UPDATED             → full tab-list snapshot (sandbox is SOT).
 *   BROWSER_TABS                     → embedded chrome tab strip (local/embedded
 *                                      lane only; every tab carries `active`).
 *
 * All events route into browserStore; BrowserCanvas reads from the
 * store reactively and connects via useNekoClient.
 */
import type { Socket } from "socket.io-client";
import { markBrowserOpenClicked } from "../../lib/open-path-metric";
import {
  EVENTS,
  type BrowserLocalReadyEventPayload,
  type BrowserLocalStoppedPayload,
  type BrowserNavigatePayload,
  type BrowserTabsPayload,
  type BrowserTabsUpdatedPayload,
  type BrowserViewStatePayload,
} from "@bash/shared";
import { isDesktopShell } from "@/lib/desktop-shell";
import { wakeEmbeddedBrowser } from "@/lib/embedded-browser";
import { useBrowserStore } from "../browserStore";
import { openComputerOnBrowserActivity } from "./browserAutoOpen";
import { useTaskStore } from "../taskStore";

// Surface ↔ lane architecture (absolute): web app = remote neko browser;
// native desktop shell = local in-app browser. The shell must never request,
// accept, or render neko streams — and the web page must never let a local
// runner's broadcast stomp its neko view.

export function attachBrowserStreamHandler(socket: Socket): () => void {
  const onTabsUpdated = (payload: BrowserTabsUpdatedPayload) => {
    if (!payload || !Array.isArray(payload.tabs)) return;
    useBrowserStore.getState().setTabs(payload.tabs);
  };
  // Local in-app browser (LOCAL_BROWSER_V1) — mirrors onReady/onStopped for
  // the loopback view server instead of Neko WebRTC. The same event also
  // announces the embedded lane (EMBEDDED_BROWSER_V1): transport==='embedded'
  // means the shell's own CEF pane owns the pixels and there is nothing to
  // dial.
  const onLocalReady = (payload: BrowserLocalReadyEventPayload) => {
    // Web is remote-only: a local runner's READY is broadcast (forward:true)
    // to every surface of the user, including an open web tab whose own
    // tasks run on Blaxel — it must not stomp the web page's neko view
    // (and the web page can't reach ws://127.0.0.1 on the user's Mac anyway).
    // This covers the embedded shape too: only the shell has a CEF view.
    if (!isDesktopShell()) {
      // eslint-disable-next-line no-console
      console.info(
        "[Browser] local stream ignored on the web — remote browser owns this surface"
      );
      return;
    }
    if (payload.transport === "embedded") {
      // Embedded: flip the lane; EmbeddedBrowserPane drives the shell's
      // show/bounds commands from the pane itself. Streaming payloads
      // (wsUrl present, transport absent/'view-server') keep the exact
      // pre-embedded path below.
      useBrowserStore.getState().setEmbeddedReady(payload);
    } else if (payload.transport === "extension") {
      // Extension: the agent is driving the operator's real Chrome. No surface
      // to render — flip the lane so the pane shows the honest "working in your
      // Chrome" state instead of a stale in-app body.
      useBrowserStore.getState().setExtensionActive(payload);
    } else {
      useBrowserStore.getState().setLocalReady(payload);
    }
    // Open the browser panel for the task the user is watching. READY is the
    // authoritative "a local browser stream is live" signal, so it must open
    // the pane itself — the tool-use gate (streamingHandlers) only opens once
    // source==='local'/'embedded', which loses the race when the browser tool fires
    // before READY arrives (a warm/reused session's very first tool). Without
    // this the pane never appears for that task. Respects hasUserClosed via
    // onAgentBrowserActivity. (Backstop re-added after a stale-base sibling
    // dropped it — 2026-07-12 founder drill.)
    const taskId = useTaskStore.getState().selectedTaskId;
    if (taskId) {
      useBrowserStore.getState().openPanelForTask(taskId);
      // Same per-world gate as the tool-fire path (browserAutoOpen).
      openComputerOnBrowserActivity(taskId);
    }
  };
  const onLocalStopped = (payload: BrowserLocalStoppedPayload) => {
    useBrowserStore.getState().setLocalStopped(payload);
  };

  // Embedded-lane tab strip (EMBEDDED_BROWSER_V1). A different channel than
  // BROWSER_TABS_UPDATED: full snapshots whose tabs all carry an `active`
  // flag, feeding the chrome's own tab strip. Shell-only — the CEF pane is
  // the only surface that renders it. Tolerant of payload growth (favicon,
  // per-tab taskId today): extra fields ride along into the store untouched.
  const onEmbeddedTabs = (payload: BrowserTabsPayload) => {
    if (!isDesktopShell()) return;
    if (!payload || !Array.isArray(payload.tabs)) return;
    useBrowserStore.getState().setEmbeddedTabs(payload.tabs);
  };

  // The server-owned view-state snapshot. Mirrored verbatim into the store
  // (monotonic-since guard lives in applyViewSnapshot); BrowserCanvas renders it.
  const onViewState = (payload: BrowserViewStatePayload) => {
    if (!payload || typeof payload.state !== "string") return;
    useBrowserStore.getState().applyViewSnapshot(payload);
  };

  socket.on(EVENTS.BROWSER_TABS_UPDATED, onTabsUpdated);
  socket.on(EVENTS.BROWSER_LOCAL_READY, onLocalReady);
  socket.on(EVENTS.BROWSER_LOCAL_STOPPED, onLocalStopped);
  socket.on(EVENTS.BROWSER_TABS, onEmbeddedTabs);
  socket.on(EVENTS.BROWSER_VIEW_STATE, onViewState);

  return () => {
    socket.off(EVENTS.BROWSER_TABS_UPDATED, onTabsUpdated);
    socket.off(EVENTS.BROWSER_LOCAL_READY, onLocalReady);
    socket.off(EVENTS.BROWSER_LOCAL_STOPPED, onLocalStopped);
    socket.off(EVENTS.BROWSER_TABS, onEmbeddedTabs);
    socket.off(EVENTS.BROWSER_VIEW_STATE, onViewState);
  };
}

/**
 * Send a tab mutation request up to the server. The server forwards it to
 * the user's sandbox, which mutates Chrome and fires a fresh
 * BROWSER_TABS_UPDATED snapshot — never mutate `tabs` locally; treat the
 * sandbox as the single source of truth.
 */
export function requestOpenTab(socket: Socket, url?: string): void {
  socket.emit(EVENTS.BROWSER_OPEN_TAB, url ? { url } : {});
}

export function requestCloseTab(socket: Socket, targetId: string): void {
  socket.emit(EVENTS.BROWSER_CLOSE_TAB, { targetId });
}

/**
 * Manually open / close the Neko browser session from the UI (the
 * computer-icon toggle in MessageInput). OPEN calls getBrowserSession()
 * which connects to Chrome CDP; CLOSE tears down the CDP connection
 * and fires BROWSER_SESSION_STOPPED → BROWSER_STREAM_STOPPED.
 */
export function requestOpenBrowserSession(socket: Socket): void {
  // Desktop shell: WARM the local CEF engine first so the runner's attach finds
  // a live surface. Without this the manual open reached the runner but it had
  // no engine to attach to → the attach polled, timed out, and "Starting up"
  // hung forever (founder, 2026-07-14 — no engine ever spawned). Warm-only
  // (hidden, about:blank); the pane does NOT mount until the runner announces
  // BROWSER_LOCAL_READY after attaching + painting, so there is no black pane.
  markBrowserOpenClicked(); // click→first-frame stamp (open-path-metric)
  if (isDesktopShell()) wakeEmbeddedBrowser();
  // Emit for BOTH surfaces — the SERVER routes it by relay origin: a native
  // desktop shell has a runner-inbound relay, so the server forwards the open
  // straight to the local runner (which attaches to the warm CEF engine +
  // announces BROWSER_LOCAL_READY, the same render path an agent open takes); a
  // web user gets the Blaxel/neko wake.
  socket.emit(EVENTS.BROWSER_OPEN_SESSION, {});
}

export function requestCloseBrowserSession(socket: Socket): void {
  socket.emit(EVENTS.BROWSER_CLOSE_SESSION, {});
}

/**
 * Drive the embedded browser's chrome (EMBEDDED_BROWSER_V1): nav buttons,
 * tab activation, new tab, and user-typed navigation all ride one event.
 * The engine mutates and answers with a fresh BROWSER_TABS snapshot —
 * same never-mutate-locally discipline as the sandbox tab requests above.
 */
export function requestBrowserNavigate(
  socket: Socket,
  payload: BrowserNavigatePayload
): void {
  socket.emit(EVENTS.BROWSER_NAVIGATE, payload);
}
